import React from "react";
import { Accordion, Button, Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { isMobile } from "react-device-detect";
import AppSocialLinks from "../../components/AppSocialLinks";
import AppStoreBadges from "../../components/AppStoreBadges";
import { HOW_TO_PATH } from "./reassistVideos";

const faqs = [
  {
    question: "Where is my data stored?",
    answer:
      "Your listings, contacts, notes, photos, and reminders are stored in a private database on your phone. On iPhone, documents you attach to a listing, and automatic backups if you turn them on, are kept in your own iCloud Drive. On Android, automatic backups go to a folder you choose, on your phone or in a cloud folder such as Google Drive. None of it is ever sent to us — we have no servers to send it to. See our privacy policy for details.",
  },
  {
    question: "How do I back up my data?",
    answer:
      "Turn on automatic backups and the app keeps your three most recent backups. On iPhone, go to Settings → Your Data → Backup & Restore; backups go to your iCloud Drive. On Android, go to Settings → Backup & Restore and pick a folder once, on your phone, an SD card, or a cloud folder such as Google Drive. The same screen can also save a backup file wherever you choose. Your phone's own backup (iCloud Backup, or Google's backup on Android) may include the app's data as well.",
  },
  {
    question: "Will I lose my data if I delete the app?",
    answer:
      "Deleting the app deletes the data stored on your phone. Automatic backups and backup files you saved are not deleted, and you can restore from one in Backup & Restore (iPhone: Settings → Your Data → Backup & Restore; Android: Settings → Backup & Restore). Before you remove or replace the app, turn on automatic backups or save a backup.",
  },
  {
    question: "Why am I not receiving reminder notifications?",
    answer:
      "Reminders are scheduled locally on your phone, so notifications must be allowed for the app. On iPhone, open Settings, find RE Assist, tap Notifications, and make sure Allow Notifications is on. On Android, open Settings → Apps → RE Assist → Notifications and make sure they're allowed. Also check that Focus or Do Not Disturb isn't silencing them.",
  },
  {
    question: "Why doesn't a listing appear on the map?",
    answer:
      "Open the map from Listings. Each address is looked up once (with Apple Maps on iPhone, Google on Android) and its pin is saved, and the map tells you how many addresses it couldn't find. If a listing is missing, check the address for typos and make sure it has a street, city, and state; when you edit it, the pin is looked up again. Picking the address from the suggestions as you type saves the pin right away. New construction and raw lots are sometimes not on the map yet. Also check the filter chips across the top of the map, which can hide Active, Pending, or Sold listings, or one side of your deals.",
  },
  {
    question: "How do I plan my day of showings?",
    answer:
      "Tap the Today card on the first tab (For You on iPhone, Dashboard on Android) to open your agenda. Tap + to add a showing: either one where you take your buyer to a home, or one where another agent shows one of your listings. Open houses appear with their times once you turn on Set Times under Open House when editing a listing, and contract deadlines and closings due that day are listed at the top. Showings with a buyer also appear on that buyer's contact page, with your notes on what they liked. You get a reminder an hour before each showing, and the Today widget shows what's left of the day.",
  },
  {
    question: "How does the route and mileage work?",
    answer:
      "On the agenda, tap Route to see the day's stops on a map, numbered in appointment order, with the driving distance and time between them (from Apple Maps on iPhone, Google on Android). Tap the car next to a stop for directions in Apple Maps, Google Maps, or Waze. Log Mileage adds the whole day to your mileage log as one trip, filed under your buyer when the showings were all for one client; the total covers the drive between stops, so add the drive from your home or office before you save if it counts. A route needs a connection.",
  },
  {
    question: "What does Under Contract on This One do?",
    answer:
      "When your buyer goes under contract on a home they toured, open that showing and tap Under Contract on This One. The app starts the Buyer's Agent deal at that address, marked Pending and linked to the client, with your showing notes on it and the client's showing-day miles moved over so profit per deal shows what landing them cost. Buyer Prep and Home Search are already ticked done. If you had already started a buyer deal for that client, it's filled in instead of making a second one.",
  },
  {
    question: "What does the app ask when a closing date passes?",
    answer:
      "When a listing's closing date has passed and it isn't marked Sold, or a contract date arrives on a deal that's still Active, the app asks: Did it close? or Under contract? One tap answers: Mark Sold, Mark Pending, set the new date if it slipped, or record that it fell through. Up to three of these show at a time. You can also change a listing's status from the badge on its card.",
  },
  {
    question: "Which reports are free, and which need Pro?",
    answer:
      "Free: your net profit for the year on Financials, after splits, spending, and miles, and the Seller Report on any listing, a PDF of its showings, feedback, and what's next that you send to your seller. With Pro, the Reports card on Financials adds the Pipeline (what your pending deals will pay and where the year ends), Quarterly Estimated Taxes (what to set aside before each due date, at a rate you set), the full Profit & Loss (month by month, against last year, as PDF or CSV), and the year-end tax packet. The tax figures are for planning; check them with your accountant.",
  },
  {
    question: "How do I change the app's colours?",
    answer:
      "On iPhone, go to Settings → Appearance and pick Clay, Blush, Sky, or Sage. The app and its widgets change at once. The For You tab and colour themes are on iPhone for now.",
  },
  {
    question: "Can the app see my photo library?",
    answer:
      "No. When you attach photos to a listing, the app uses your phone's photo picker (Apple's on iPhone, Android's on Android), which only shares the specific photos you choose. The app never has access to your full photo library.",
  },
  {
    question: "Why does the app ask for calendar access?",
    answer:
      "On iPhone, the one-tap Add to Calendar uses write-only access — it can create events but can't see your existing calendar — and Pro calendar sync uses full access solely to keep the events it created up to date. On Android, closing events go to the calendar you choose in Settings; the app reads your calendars only to list them for that choice and to keep its own events up to date. No calendar data is sent anywhere.",
  },
  {
    question: "Does the app work offline?",
    answer:
      "Yes. Your listings live on your phone, so you can view and edit everything offline. A few things need a connection: the listing map, the day's route, and address suggestions (Apple Maps on iPhone, Google on Android); on iPhone, street photos and syncing with iCloud Drive; on both, automatic backups to a cloud folder. Without a connection the route still lists your stops, and you can type the miles yourself.",
  },
];

const RealEstateAssistSupport = () => {

  return (
    <Container fluid style={styles.container}>
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8} style={styles.content}>
            <h1 style={styles.title}>Real Estate Agent Assist Support</h1>
            <p style={styles.subtitle}>
              Answers to common questions about Real Estate Agent Assist. Can&apos;t
              find what you&apos;re looking for? Reach out and we&apos;ll get
              back to you within one business day.
            </p>
            <p style={styles.subtitle}>
              New to the app? Watch the{" "}
              <Link to={HOW_TO_PATH} className="footer-link" style={styles.inlineLink}>
                how-to videos
              </Link>
              : adding your first listing, contract deadlines, your split and
              cap, importing from your MLS, and backups.
            </p>

            <h2 style={styles.heading}>Frequently Asked Questions</h2>
            <Accordion flush className="support-accordion mt-3">
              {faqs.map((faq, index) => (
                <Accordion.Item
                  eventKey={String(index)}
                  key={faq.question}
                  style={styles.accordionItem}
                >
                  <Accordion.Header>{faq.question}</Accordion.Header>
                  <Accordion.Body style={styles.accordionBody}>
                    {faq.answer}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>

            <h2 style={styles.heading}>Send Feedback</h2>
            <p style={styles.paragraph}>
              Found a bug, have a feature idea, or just want to tell us how
              the app is working for you? Send us a message and we&apos;ll get
              back to you within one business day. Include your phone model,
              its iOS or Android version, and a short description of what
              happened.
            </p>
            <div style={styles.ctaRow}>
              <Button
                variant="primary"
                as={Link}
                to="/contact?type=support"
                style={styles.ctaButton}
              >
                Send Feedback
              </Button>
            </div>

            <h2 style={styles.heading}>Get the App</h2>
            <p style={styles.paragraph}>
              Real Estate Agent Assist is available on the App Store and Google
              Play. If it&apos;s
              helping you close deals, a rating or review goes a long way.
            </p>
            <AppStoreBadges height={isMobile ? 44 : 50} align="left" />
            <div style={styles.socialRow}>
              <AppSocialLinks label="Follow @reassistapp" align="left" />
            </div>
            <p style={styles.linkRow}>
              <Link to="/apps/real-estate-assist" className="footer-link">
                About Real Estate Agent Assist
              </Link>
              {" · "}
              <Link to={HOW_TO_PATH} className="footer-link">
                How-to videos
              </Link>
              {" · "}
              <Link to="/apps/real-estate-assist/privacy" className="footer-link">
                Privacy Policy
              </Link>
            </p>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};

export default RealEstateAssistSupport;

const styles = {
  appStoreBadge: {
    height: isMobile ? 48 : 56,
  },
  ctaRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  inlineLink: {
    fontSize: "inherit",
    textDecoration: "underline",
  },
  socialRow: {
    marginTop: 24,
  },
  storeRow: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
  },
  accordionBody: {
    backgroundColor: "#1C1816",
    color: "#ACACAC",
    fontSize: 16,
    lineHeight: 1.7,
    textAlign: "left",
  },
  accordionItem: {
    backgroundColor: "#1C1816",
    marginBottom: 8,
  },
  container: {
    backgroundColor: "#000",
    paddingTop: 100,
    paddingBottom: 100,
  },
  content: {
    textAlign: "left",
  },
  ctaButton: {
    minWidth: 170,
    fontWeight: 600,
  },
  heading: {
    color: "#CF3620",
    fontSize: 24,
    fontWeight: 700,
    marginTop: 40,
    marginBottom: 12,
  },
  linkRow: {
    color: "#ACACAC",
    fontSize: 16,
    marginTop: 32,
  },
  paragraph: {
    color: "#ACACAC",
    fontSize: 18,
    lineHeight: 1.7,
    marginBottom: 20,
  },
  subtitle: {
    color: "#ACACAC",
    fontSize: 18,
    marginBottom: 8,
  },
  title: {
    color: "#FFF",
    fontWeight: 700,
    marginBottom: 12,
  },
};
